import { BILLING_STATE_VALUE_LABEL_MAP } from "features/Common/constants/billingConstants";
import { checkIsArrayEmpty } from "features/Common/modules/DataTypes/utils/Array.utils";
import { checkIsStringEmpty } from "features/Common/modules/DataTypes/Strings.utils";
import { app_route_keys } from "constants/urlPaths";
import { navigateTo } from "utils/urlUtils";
import {
  QR_PAYMENT_STATUS_CODES,
  QR_STATUS_TEXT,
  QR_CONVERSION_TEXT,
  QR_FILTER_KEYS,
  QR_CONVERTED_STRINGS,
  DEFAULT_QUANTITY,
  APPOINTMENT_PLACEHOLDER_VALUE,
} from "../constants/qrPayments.constants";

export const mapQrPaymentListResponse = (pay) => {
  return {
    ...pay,
    id: pay.qr_order_uuid,
    upi_id: pay.payer_vpa,
    transaction_uuid: pay.qr_order_uuid,
    transaction_date: pay.paid_at,
    customer_name: pay.customer_full_name || "",
    customer_email: pay.customer_email || "",
    customer_phone: pay.customer_phone || "",
    link_title: pay.link_title || "",
    qr_payment_status:
      pay.status === QR_PAYMENT_STATUS_CODES.SUCCESS
        ? QR_STATUS_TEXT.SUCCESS
        : QR_STATUS_TEXT.FAILED,
    invoice_status: pay.is_converted
      ? QR_CONVERSION_TEXT.CONVERTED
      : QR_CONVERSION_TEXT.PENDING,
  };
};

export const normalizeQrFilters = (filter = {}) => {
  const normalized = { ...filter };
  for (let key in normalized) {
    if (typeof normalized[key] === "string") {
      normalized[key] = normalized[key].trim();
    }
  }
  const isConvertedVal = normalized[QR_FILTER_KEYS.IS_CONVERTED];
  if (!checkIsStringEmpty(isConvertedVal)) {
    delete normalized[QR_FILTER_KEYS.IS_CONVERTED];
  }
  delete normalized[QR_FILTER_KEYS.UNCLAIMED_ONLY];
  return normalized;
};

export const parseBooleanFilter = (val) => {
  return val === QR_CONVERTED_STRINGS.TRUE || val === true;
};

export const getBillingStateName = (values) => {
  const billingStateCode =
    values?.customer_gst_details?.billing_state ||
    values?.billing_state ||
    values?.state;
  return billingStateCode
    ? BILLING_STATE_VALUE_LABEL_MAP[billingStateCode] || billingStateCode
    : "";
};

export const getQrOrderUuid = (record) => {
  return record?.qr_order_uuid || record?.transaction_uuid || record?.id;
};

export const buildQrListingMapPayload = (values, listingData) => {
  const listingUuid =
    values?.listing_uid || values?.listing_data?.uuid || listingData?.uuid;

  if (!listingUuid)
    return { listingUuid: undefined, listingMapPayload: undefined };

  const listingMapItem = {
    quantity: values?.selected_quantity || DEFAULT_QUANTITY,
  };

  // 1. Batch / Class Selection
  if (values?.selected_batch) {
    let batchVal = values.selected_batch;
    if (typeof batchVal === "string" && batchVal.startsWith("{")) {
      try {
        const parsed = JSON.parse(batchVal);
        if (parsed.classUuid) {
          listingMapItem.class_uid = parsed.classUuid;
        }
        if (parsed.slotUuids) {
          listingMapItem.slot_uids = parsed.slotUuids;
        }
      } catch (e) {
        listingMapItem.class_uid = batchVal;
      }
    } else {
      listingMapItem.class_uid = batchVal;
    }
  }

  // 2. Appointment / Webinar Slot Selection
  if (Array.isArray(values?.selected_appointment_slots)) {
    const slotUuids = values.selected_appointment_slots
      .map((slot) => slot?.uuid)
      .filter(
        (uuid) => Boolean(uuid) && uuid !== APPOINTMENT_PLACEHOLDER_VALUE
      );

    if (!checkIsArrayEmpty(slotUuids)) {
      listingMapItem.slot_uids = slotUuids;
    }
  }

  // 3. Variant selection
  if (values?.offering_variant_uuid) {
    listingMapItem.offering_variant_uuid = values.offering_variant_uuid;
  }

  // 4. Membership selection
  if (values?.membership_uuid) {
    listingMapItem.membership_uuid =
      typeof values.membership_uuid === "object"
        ? values.membership_uuid.value
        : values.membership_uuid;
  }

  return {
    listingUuid,
    listingMapPayload: {
      [listingUuid]: listingMapItem,
    },
  };
};

/**
 * Builds navigation route parameters for QR Payments List given a filter object or string.
 * Centralizes filter serialization for navigation.
 * @param {Object|string} filter - Filter object (e.g. { status: QR_PAYMENT_STATUS_CODES.SUCCESS })
 * @returns {Object} Navigation route key and options object for navigateTo
 */
export const buildQrListUrl = (filter = {}) => {
  const args =
    typeof filter === "object"
      ? encodeURIComponent(JSON.stringify(filter))
      : filter;
  return {
    key: app_route_keys.qr_payments_list,
    options: { args },
  };
};

/**
 * Navigates to the QR Payments List page with the specified filter.
 * @param {Object|string} filter - Filter parameters
 */
export const navigateToQrPaymentsList = (filter = {}) => {
  const { key, options } = buildQrListUrl(filter);
  navigateTo(key, options);
};
